import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import styled from "styled-components";
import {
  AlertError,
  AlertSuccess,
  AlertQuestion,
  Modal_Component,
  Loadingfunc,
} from "../../../../component";
import {
  Label,
  COLOR_SECONDARY,
  COLOR_PRIMARY,
  ButtonPrimary,
} from "../../../../component/style/content/default";
import { CompanyService } from "../../../../service";

export const Modal_payment_gateway = ({
  show,
  onHide,
  company_id,
  company_name,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [gatewayData, setGatewayData] = useState({
    ms_payment_identifier: null,
    configuration_source: "ENV_FALLBACK",
    gateways: [],
  });

  const [viewMode, setViewMode] = useState("list"); // "list" | "select_provider" | "form"
  const [editingProvider, setEditingProvider] = useState(null); // "MIDTRANS" | "XENDIT"
  const [showSecret, setShowSecret] = useState(false);
  const [availableList, setAvailableList] = useState([]);

  // Form states
  const [formMidtrans, setFormMidtrans] = useState({
    merchant_id: "",
    client_key: "",
    server_key: "",
    is_production: false,
    active: true,
  });

  const [formXendit, setFormXendit] = useState({
    secret_api_key: "",
    webhook_verification_token: "",
    default_channel_code: "ID_DANA",
    country: "ID",
    currency: "IDR",
    channel_properties_list: [], // [{ key: "", value: "" }]
    active: true,
  });

  const getApiErrorMessage = (result, fallback) =>
    result?.message || result?.error || fallback;

  const requireApiSuccess = (result, fallback) => {
    if (!result?.success) {
      throw new Error(getApiErrorMessage(result, fallback));
    }
    return result;
  };

  const loadData = async () => {
    if (!company_id) return;
    setLoading(true);
    try {
      const res = requireApiSuccess(
        await CompanyService.get_company_payment_gateways({ company_id }),
        "Gagal memuat konfigurasi payment gateway"
      );
      setGatewayData(res.data);
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Gagal memuat konfigurasi payment gateway";
      AlertError({ title: "ERROR", text: errMsg });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && company_id) {
      setViewMode("list");
      setEditingProvider(null);
      setShowSecret(false);
      loadData();
    }
  }, [show, company_id]);

  const handleOpenAddGateway = async () => {
    setLoading(true);
    try {
      const res = requireApiSuccess(
        await CompanyService.get_available_payment_gateways({ company_id }),
        "Gagal memuat daftar payment gateway"
      );
      if (res?.data && Array.isArray(res.data)) {
        setAvailableList(res.data);
      } else {
        const supported = ["MIDTRANS", "XENDIT"];
        const list = supported.map((id) => {
          const gw = gatewayData.gateways?.find(
            (g) => g.ms_payment_identifier === id
          );
          return {
            ms_payment_identifier: id,
            name: id === "MIDTRANS" ? "Midtrans" : "Xendit",
            already_configured: Boolean(gw?.configured),
          };
        });
        setAvailableList(list);
      }
      setViewMode("select_provider");
    } catch (err) {
      const supported = ["MIDTRANS", "XENDIT"];
      const list = supported.map((id) => {
        const gw = gatewayData.gateways?.find(
          (g) => g.ms_payment_identifier === id
        );
        return {
          ms_payment_identifier: id,
          name: id === "MIDTRANS" ? "Midtrans" : "Xendit",
          already_configured: Boolean(gw?.configured),
        };
      });
      setAvailableList(list);
      setViewMode("select_provider");
    } finally {
      setLoading(false);
    }
  };

  const openForm = (provider) => {
    setEditingProvider(provider);
    setShowSecret(false);

    const existing = gatewayData.gateways?.find(
      (g) => g.ms_payment_identifier === provider
    );

    if (provider === "MIDTRANS") {
      setFormMidtrans({
        merchant_id: existing?.public_configuration?.merchant_id || "",
        client_key: "",
        server_key: "",
        is_production: Boolean(existing?.public_configuration?.is_production),
        active: existing ? Boolean(existing.active) : true,
      });
    } else if (provider === "XENDIT") {
      const pub = existing?.public_configuration || {};
      const channelPropsList = [];
      if (
        pub.default_channel_properties &&
        typeof pub.default_channel_properties === "object"
      ) {
        Object.entries(pub.default_channel_properties).forEach(([k, v]) => {
          channelPropsList.push({
            key: k,
            value: typeof v === "object" ? JSON.stringify(v) : String(v),
          });
        });
      }
      setFormXendit({
        secret_api_key: "",
        webhook_verification_token: "",
        default_channel_code: pub.default_channel_code || "ID_DANA",
        country: pub.country || "ID",
        currency: pub.currency || "IDR",
        channel_properties_list: channelPropsList,
        active: existing ? Boolean(existing.active) : true,
      });
    }

    setViewMode("form");
  };

  const handleSelectGateway = async (identifier) => {
    const isEnvFallback = !identifier;
    const confirm = await AlertQuestion({
      title: "Konfirmasi Pemilihan Gateway",
      text: isEnvFallback
        ? "Apakah Anda yakin ingin menggunakan konfigurasi MIDTRANS global dari environment (.env)?"
        : `Apakah Anda yakin ingin memilih ${identifier} sebagai payment gateway aktif perusahaan ini?`,
    });

    if (!confirm?.isConfirmed) return;

    setSubmitting(true);
    try {
      const res = requireApiSuccess(
        await CompanyService.put_company_payment_gateway_selection({
          company_id,
          body: { ms_payment_identifier: identifier },
        }),
        "Gagal memilih payment gateway"
      );
      await AlertSuccess({
        title: "BERHASIL",
        text: res?.success || "Pilihan payment gateway berhasil diperbarui",
      });
      await loadData();
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Gagal memilih payment gateway";
      AlertError({ title: "ERROR", text: errMsg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteGateway = async (identifier) => {
    const confirm = await AlertQuestion({
      title: "Konfirmasi Hapus",
      text: `Apakah Anda yakin ingin menghapus konfigurasi payment gateway ${identifier}? Data credential akan dihapus permanen.`,
    });

    if (!confirm?.isConfirmed) return;

    setSubmitting(true);
    try {
      const res = requireApiSuccess(
        await CompanyService.delete_company_payment_gateway({
          company_id,
          ms_payment_identifier: identifier,
        }),
        "Gagal menghapus konfigurasi payment gateway"
      );
      await AlertSuccess({
        title: "BERHASIL",
        text: res?.success || "Konfigurasi payment gateway berhasil dihapus",
      });
      await loadData();
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Gagal menghapus konfigurasi payment gateway";
      AlertError({ title: "ERROR", text: errMsg });
    } finally {
      setSubmitting(false);
    }
  };

  // Submit Midtrans Form
  const handleSubmitMidtrans = async (e) => {
    if (e) e.preventDefault();
    const existing = gatewayData.gateways?.find(
      (g) => g.ms_payment_identifier === "MIDTRANS"
    );
    const isNew = !existing || !existing.configured;

    if (isNew) {
      if (!formMidtrans.client_key.trim() || !formMidtrans.server_key.trim()) {
        AlertError({
          title: "VALIDASI GAGAL",
          text: "Client Key dan Server Key wajib diisi untuk konfigurasi baru MIDTRANS",
        });
        return;
      }
    }

    const configPayload = {
      is_production: Boolean(formMidtrans.is_production),
    };
    if (formMidtrans.merchant_id.trim()) {
      configPayload.merchant_id = formMidtrans.merchant_id.trim();
    }
    if (formMidtrans.client_key.trim()) {
      configPayload.client_key = formMidtrans.client_key.trim();
    }
    if (formMidtrans.server_key.trim()) {
      configPayload.server_key = formMidtrans.server_key.trim();
    }

    const payload = {
      active: Boolean(formMidtrans.active),
      configuration: configPayload,
    };

    setSubmitting(true);
    try {
      const result = isNew
        ? await CompanyService.post_company_payment_gateway({
            company_id,
            body: {
              ...payload,
              ms_payment_identifier: "MIDTRANS",
            },
          })
        : await CompanyService.put_company_payment_gateway({
            company_id,
            ms_payment_identifier: "MIDTRANS",
            body: payload,
          });
      const res = requireApiSuccess(
        result,
        "Gagal menyimpan konfigurasi MIDTRANS"
      );

      await AlertSuccess({
        title: "BERHASIL",
        text: res?.success || "Konfigurasi MIDTRANS berhasil disimpan",
      });
      setViewMode("list");
      await loadData();
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Gagal menyimpan konfigurasi MIDTRANS";
      AlertError({ title: "ERROR", text: errMsg });
    } finally {
      setSubmitting(false);
    }
  };

  // Submit Xendit Form
  const handleSubmitXendit = async (e) => {
    if (e) e.preventDefault();
    const existing = gatewayData.gateways?.find(
      (g) => g.ms_payment_identifier === "XENDIT"
    );
    const isNew = !existing || !existing.configured;

    if (isNew) {
      if (
        !formXendit.secret_api_key.trim() ||
        !formXendit.webhook_verification_token.trim() ||
        !formXendit.default_channel_code.trim()
      ) {
        AlertError({
          title: "VALIDASI GAGAL",
          text: "Secret API Key, Webhook Verification Token, dan Default Channel Code wajib diisi untuk konfigurasi baru XENDIT",
        });
        return;
      }
    }

    const defaultChannelProps = {};
    formXendit.channel_properties_list.forEach((item) => {
      const key = item.key?.trim();
      if (key) {
        try {
          defaultChannelProps[key] = JSON.parse(item.value);
        } catch {
          defaultChannelProps[key] = item.value;
        }
      }
    });

    const configPayload = {
      default_channel_code: formXendit.default_channel_code.trim() || "ID_DANA",
      country: formXendit.country.trim() || "ID",
      currency: formXendit.currency.trim() || "IDR",
      default_channel_properties: defaultChannelProps,
    };

    if (formXendit.secret_api_key.trim()) {
      configPayload.secret_api_key = formXendit.secret_api_key.trim();
    }
    if (formXendit.webhook_verification_token.trim()) {
      configPayload.webhook_verification_token =
        formXendit.webhook_verification_token.trim();
    }

    const payload = {
      active: Boolean(formXendit.active),
      configuration: configPayload,
    };

    setSubmitting(true);
    try {
      const result = isNew
        ? await CompanyService.post_company_payment_gateway({
            company_id,
            body: {
              ...payload,
              ms_payment_identifier: "XENDIT",
            },
          })
        : await CompanyService.put_company_payment_gateway({
            company_id,
            ms_payment_identifier: "XENDIT",
            body: payload,
          });
      const res = requireApiSuccess(
        result,
        "Gagal menyimpan konfigurasi XENDIT"
      );

      await AlertSuccess({
        title: "BERHASIL",
        text: res?.success || "Konfigurasi XENDIT berhasil disimpan",
      });
      setViewMode("list");
      await loadData();
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Gagal menyimpan konfigurasi XENDIT";
      AlertError({ title: "ERROR", text: errMsg });
    } finally {
      setSubmitting(false);
    }
  };

  // Channel properties repeater helpers
  const handleAddChannelProp = () => {
    setFormXendit({
      ...formXendit,
      channel_properties_list: [
        ...formXendit.channel_properties_list,
        { key: "", value: "" },
      ],
    });
  };

  const handleRemoveChannelProp = (idx) => {
    const updated = formXendit.channel_properties_list.filter(
      (_, i) => i !== idx
    );
    setFormXendit({
      ...formXendit,
      channel_properties_list: updated,
    });
  };

  const handleChangeChannelProp = (idx, field, val) => {
    const updated = [...formXendit.channel_properties_list];
    updated[idx][field] = val;
    setFormXendit({
      ...formXendit,
      channel_properties_list: updated,
    });
  };

  const isEnvSelected = gatewayData.ms_payment_identifier === null;

  return (
    <Modal_Component
      show={show}
      onHide={onHide}
      size="lg"
      title={`Payment Gateway - ${company_name || ""}`}
      btnSubmit={false}
      closeText="Tutup"
    >
      <div className="container py-2">
        {/* Banner Overview Selection */}
        <BannerCard className="p-3 mb-3">
          <div className="d-flex flex-wrap justify-content-between align-items-center">
            <div>
              <div className="text-muted small">Status Gateway Aktif:</div>
              <div className="d-flex align-items-center mt-1">
                {isEnvSelected ? (
                  <BadgeWarning>MIDTRANS (ENV Fallback)</BadgeWarning>
                ) : (
                  <BadgeSuccess>
                    {gatewayData.ms_payment_identifier} (Company Database)
                  </BadgeSuccess>
                )}
              </div>
              <div className="text-muted small mt-2">
                {isEnvSelected
                  ? "Transaksi loyalty menggunakan akun MIDTRANS global dari environment server (.env)."
                  : `Transaksi loyalty menggunakan kredensial mandiri ${gatewayData.ms_payment_identifier} milik perusahaan ini.`}
              </div>
            </div>

            <div className="mt-2 mt-md-0">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={isEnvSelected || submitting || loading}
                onClick={() => handleSelectGateway(null)}
              >
                Gunakan MIDTRANS dari ENV
              </button>
            </div>
          </div>
        </BannerCard>

        {loading ? (
          <div className="py-4 text-center">
            <Loadingfunc />
          </div>
        ) : viewMode === "list" ? (
          /* ================= LIST VIEW ================= */
          <div>
            <div className="mb-3 d-flex flex-wrap justify-content-between align-items-center">
              <div className="fw-bold" style={{ color: COLOR_SECONDARY, fontSize: "16px" }}>
                Daftar Payment Gateway
              </div>
              <ButtonPrimary
                type="button"
                onClick={handleOpenAddGateway}
                disabled={submitting || loading}
              >
                + Tambah Gateway
              </ButtonPrimary>
            </div>

            <div className="row g-3">
              {(gatewayData.gateways || []).map((gw) => {
                const isSelected =
                  gw.selected ||
                  gw.ms_payment_identifier === gatewayData.ms_payment_identifier;
                const isConfigured = Boolean(gw.configured);
                const isActive = Boolean(gw.active);

                return (
                  <div className="col-12" key={gw.ms_payment_identifier}>
                    <GatewayCard
                      className="p-3"
                      $isSelected={isSelected}
                      $isConfigured={isConfigured}
                    >
                      <div className="d-flex flex-wrap justify-content-between align-items-start mb-2">
                        <div>
                          <h5 className="mb-1 fw-bold text-dark">
                            {gw.ms_payment_identifier}
                          </h5>
                          <div className="d-flex flex-wrap gap-1 align-items-center">
                            {isSelected && (
                              <BadgeSuccess>Terpilih</BadgeSuccess>
                            )}
                            {isConfigured ? (
                              isActive ? (
                                <BadgeInfo>Aktif</BadgeInfo>
                              ) : (
                                <BadgeWarning>Nonaktif</BadgeWarning>
                              )
                            ) : (
                              <BadgeMuted>Belum Dikonfigurasi</BadgeMuted>
                            )}
                          </div>
                        </div>

                        <div className="d-flex flex-wrap gap-2 mt-2 mt-md-0">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            disabled={submitting}
                            onClick={() => openForm(gw.ms_payment_identifier)}
                          >
                            {isConfigured ? "Ubah" : "Konfigurasi"}
                          </button>

                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            disabled={
                              !isConfigured ||
                              !isActive ||
                              isSelected ||
                              submitting
                            }
                            onClick={() =>
                              handleSelectGateway(gw.ms_payment_identifier)
                            }
                          >
                            Pilih
                          </button>

                          {isConfigured && !isSelected && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              disabled={submitting}
                              onClick={() =>
                                handleDeleteGateway(gw.ms_payment_identifier)
                              }
                            >
                              Hapus
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Detail Ringkasan Metadata Publik */}
                      <div className="mt-3 p-2 bg-light rounded text-secondary small">
                        {gw.ms_payment_identifier === "MIDTRANS" && (
                          <div className="row g-2">
                            <div className="col-sm-6">
                              <b>Environment:</b>{" "}
                              {gw.public_configuration?.is_production
                                ? "Production"
                                : "Sandbox"}
                            </div>
                            <div className="col-sm-6">
                              <b>Client Key:</b>{" "}
                              {gw.public_configuration?.client_key_configured
                                ? "✓ Terkonfigurasi"
                                : "-"}
                            </div>
                            <div className="col-sm-6">
                              <b>Server Key:</b>{" "}
                              {gw.public_configuration?.server_key_configured
                                ? "✓ Terkonfigurasi"
                                : "-"}
                            </div>
                            <div className="col-sm-6">
                              <b>Merchant ID:</b>{" "}
                              {gw.public_configuration?.merchant_id_configured
                                ? "✓ Terkonfigurasi"
                                : "-"}
                            </div>
                          </div>
                        )}

                        {gw.ms_payment_identifier === "XENDIT" && (
                          <div className="row g-2">
                            <div className="col-sm-6">
                              <b>Country / Currency:</b>{" "}
                              {gw.public_configuration?.country || "ID"} /{" "}
                              {gw.public_configuration?.currency || "IDR"}
                            </div>
                            <div className="col-sm-6">
                              <b>Default Channel:</b>{" "}
                              {gw.public_configuration?.default_channel_code ||
                                "-"}
                            </div>
                            <div className="col-sm-6">
                              <b>Secret API Key:</b>{" "}
                              {gw.public_configuration?.secret_api_key_configured
                                ? "✓ Terkonfigurasi"
                                : "-"}
                            </div>
                            <div className="col-sm-6">
                              <b>Webhook Token:</b>{" "}
                              {gw.public_configuration
                                ?.webhook_verification_token_configured
                                ? "✓ Terkonfigurasi"
                                : "-"}
                            </div>
                            <div className="col-12 mt-1">
                              <b>URL Callback Webhook:</b>{" "}
                              <code>/api/v1/transaction/notification/xendit</code>
                            </div>
                          </div>
                        )}
                      </div>
                    </GatewayCard>
                  </div>
                );
              })}
            </div>
          </div>
        ) : viewMode === "select_provider" ? (
          /* ================= SELECT PROVIDER VIEW ================= */
          <div>
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setViewMode("list")}
                disabled={submitting}
              >
                &larr; Kembali ke Daftar Gateway
              </button>
              <h5 className="mb-0 fw-bold" style={{ color: COLOR_PRIMARY }}>
                Tambah Payment Gateway
              </h5>
            </div>

            <div className="card p-3 mb-3 border-0 shadow-sm">
              <div className="text-secondary small mb-3">
                Pilih payment gateway yang didukung untuk ditambahkan ke perusahaan Anda:
              </div>

              <div className="row g-3">
                {(availableList || []).map((item) => {
                  const isConfigured = Boolean(item.already_configured);
                  return (
                    <div className="col-md-6" key={item.ms_payment_identifier}>
                      <ProviderChoiceCard
                        className="p-3 h-100 d-flex flex-column justify-content-between"
                        $isConfigured={isConfigured}
                      >
                        <div>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="fw-bold mb-0 text-dark">
                              {item.name || item.ms_payment_identifier}
                            </h5>
                            {isConfigured ? (
                              <BadgeSuccess>Sudah Dikonfigurasi</BadgeSuccess>
                            ) : (
                              <BadgeInfo>Tersedia</BadgeInfo>
                            )}
                          </div>
                          <div className="text-muted small">
                            {item.ms_payment_identifier === "MIDTRANS"
                              ? "Layanan payment gateway untuk kartu kredit, QRIS, Virtual Account, dan Snap token."
                              : "Layanan payment gateway untuk e-wallet (DANA, OVO, ShopeePay), QRIS, dan API v3."}
                          </div>
                        </div>

                        <div className="mt-3">
                          {isConfigured ? (
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm w-100"
                              onClick={() => openForm(item.ms_payment_identifier)}
                            >
                              Ubah Konfigurasi
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-primary btn-sm w-100"
                              onClick={() => openForm(item.ms_payment_identifier)}
                            >
                              + Konfigurasi & Tambahkan
                            </button>
                          )}
                        </div>
                      </ProviderChoiceCard>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          /* ================= FORM VIEW ================= */
          <div>
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setViewMode("list")}
                disabled={submitting}
              >
                &larr; Kembali ke Daftar Gateway
              </button>
              <h5 className="mb-0 fw-bold" style={{ color: COLOR_PRIMARY }}>
                Konfigurasi {editingProvider}
              </h5>
            </div>

            {/* FORM MIDTRANS */}
            {editingProvider === "MIDTRANS" && (
              <form onSubmit={handleSubmitMidtrans}>
                <div className="card p-3 mb-3 border-0 shadow-sm">
                  <div className="mb-3">
                    <Label color={COLOR_SECONDARY}>Merchant ID (Opsional)</Label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Contoh: G123456789"
                      value={formMidtrans.merchant_id}
                      onChange={(e) =>
                        setFormMidtrans({
                          ...formMidtrans,
                          merchant_id: e.target.value,
                        })
                      }
                      disabled={submitting}
                    />
                  </div>

                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <Label color={COLOR_SECONDARY}>
                        Client Key{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-decoration-none"
                        onClick={() => setShowSecret(!showSecret)}
                      >
                        {showSecret ? "Sembunyikan" : "Tampilkan"}
                      </button>
                    </div>
                    <input
                      type={showSecret ? "text" : "password"}
                      className="form-control"
                      placeholder={
                        gatewayData.gateways?.find(
                          (g) => g.ms_payment_identifier === "MIDTRANS"
                        )?.configured
                          ? "Kosongkan jika mempertahankan nilai lama"
                          : "Contoh: Mid-client-..."
                      }
                      value={formMidtrans.client_key}
                      onChange={(e) =>
                        setFormMidtrans({
                          ...formMidtrans,
                          client_key: e.target.value,
                        })
                      }
                      disabled={submitting}
                    />
                  </div>

                  <div className="mb-3">
                    <Label color={COLOR_SECONDARY}>
                      Server Key <span className="text-danger">*</span>
                    </Label>
                    <input
                      type={showSecret ? "text" : "password"}
                      className="form-control"
                      placeholder={
                        gatewayData.gateways?.find(
                          (g) => g.ms_payment_identifier === "MIDTRANS"
                        )?.configured
                          ? "Kosongkan jika mempertahankan nilai lama"
                          : "Contoh: Mid-server-..."
                      }
                      value={formMidtrans.server_key}
                      onChange={(e) =>
                        setFormMidtrans({
                          ...formMidtrans,
                          server_key: e.target.value,
                        })
                      }
                      disabled={submitting}
                    />
                  </div>

                  <div className="row mt-2">
                    <div className="col-md-6 mb-3">
                      <Label color={COLOR_SECONDARY}>Mode Lingkungan</Label>
                      <Form.Check
                        type="switch"
                        id="midtrans-is-production"
                        label={
                          formMidtrans.is_production
                            ? "Production Mode"
                            : "Sandbox (Development) Mode"
                        }
                        checked={formMidtrans.is_production}
                        onChange={(e) =>
                          setFormMidtrans({
                            ...formMidtrans,
                            is_production: e.target.checked,
                          })
                        }
                        disabled={submitting}
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <Label color={COLOR_SECONDARY}>Status Konfigurasi</Label>
                      <Form.Check
                        type="switch"
                        id="midtrans-active"
                        label={
                          formMidtrans.active
                            ? "Aktif (Dapat Dipilih)"
                            : "Nonaktif"
                        }
                        checked={formMidtrans.active}
                        onChange={(e) =>
                          setFormMidtrans({
                            ...formMidtrans,
                            active: e.target.checked,
                          })
                        }
                        disabled={submitting}
                      />
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-light border"
                    onClick={() => setViewMode("list")}
                    disabled={submitting}
                  >
                    Batal
                  </button>
                  <ButtonPrimary type="submit" disabled={submitting}>
                    {submitting ? "Menyimpan..." : "Simpan Konfigurasi MIDTRANS"}
                  </ButtonPrimary>
                </div>
              </form>
            )}

            {/* FORM XENDIT */}
            {editingProvider === "XENDIT" && (
              <form onSubmit={handleSubmitXendit}>
                <div className="card p-3 mb-3 border-0 shadow-sm">
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <Label color={COLOR_SECONDARY}>
                        Secret API Key <span className="text-danger">*</span>
                      </Label>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-decoration-none"
                        onClick={() => setShowSecret(!showSecret)}
                      >
                        {showSecret ? "Sembunyikan" : "Tampilkan"}
                      </button>
                    </div>
                    <input
                      type={showSecret ? "text" : "password"}
                      className="form-control"
                      placeholder={
                        gatewayData.gateways?.find(
                          (g) => g.ms_payment_identifier === "XENDIT"
                        )?.configured
                          ? "Kosongkan jika mempertahankan nilai lama"
                          : "Contoh: xnd_development_..."
                      }
                      value={formXendit.secret_api_key}
                      onChange={(e) =>
                        setFormXendit({
                          ...formXendit,
                          secret_api_key: e.target.value,
                        })
                      }
                      disabled={submitting}
                    />
                  </div>

                  <div className="mb-3">
                    <Label color={COLOR_SECONDARY}>
                      Webhook Verification Token{" "}
                      <span className="text-danger">*</span>
                    </Label>
                    <input
                      type={showSecret ? "text" : "password"}
                      className="form-control"
                      placeholder={
                        gatewayData.gateways?.find(
                          (g) => g.ms_payment_identifier === "XENDIT"
                        )?.configured
                          ? "Kosongkan jika mempertahankan nilai lama"
                          : "Token verifikasi webhook dari Xendit dashboard"
                      }
                      value={formXendit.webhook_verification_token}
                      onChange={(e) =>
                        setFormXendit({
                          ...formXendit,
                          webhook_verification_token: e.target.value,
                        })
                      }
                      disabled={submitting}
                    />
                  </div>

                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <Label color={COLOR_SECONDARY}>
                        Default Channel Code{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Contoh: ID_DANA / EWALLET"
                        value={formXendit.default_channel_code}
                        onChange={(e) =>
                          setFormXendit({
                            ...formXendit,
                            default_channel_code: e.target.value,
                          })
                        }
                        disabled={submitting}
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <Label color={COLOR_SECONDARY}>Negara (Country)</Label>
                      <input
                        type="text"
                        className="form-control"
                        value={formXendit.country}
                        onChange={(e) =>
                          setFormXendit({
                            ...formXendit,
                            country: e.target.value,
                          })
                        }
                        disabled={submitting}
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <Label color={COLOR_SECONDARY}>Mata Uang (Currency)</Label>
                      <input
                        type="text"
                        className="form-control"
                        value={formXendit.currency}
                        onChange={(e) =>
                          setFormXendit({
                            ...formXendit,
                            currency: e.target.value,
                          })
                        }
                        disabled={submitting}
                      />
                    </div>
                  </div>

                  {/* Dynamic Key-Value Repeater for default_channel_properties */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <Label color={COLOR_SECONDARY}>
                        Channel Properties (Advanced)
                      </Label>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleAddChannelProp}
                        disabled={submitting}
                      >
                        + Tambah Property
                      </button>
                    </div>

                    {formXendit.channel_properties_list.length === 0 ? (
                      <div className="text-muted small fst-italic">
                        Tidak ada channel property tambahan (opsional).
                      </div>
                    ) : (
                      formXendit.channel_properties_list.map((prop, idx) => (
                        <div
                          className="d-flex gap-2 align-items-center mb-2"
                          key={idx}
                        >
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Key (contoh: success_return_url)"
                            value={prop.key}
                            onChange={(e) =>
                              handleChangeChannelProp(idx, "key", e.target.value)
                            }
                            disabled={submitting}
                          />
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Value"
                            value={prop.value}
                            onChange={(e) =>
                              handleChangeChannelProp(
                                idx,
                                "value",
                                e.target.value
                              )
                            }
                            disabled={submitting}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleRemoveChannelProp(idx)}
                            disabled={submitting}
                            title="Hapus baris"
                          >
                            &times;
                          </button>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="mb-3">
                    <Label color={COLOR_SECONDARY}>Status Konfigurasi</Label>
                    <Form.Check
                      type="switch"
                      id="xendit-active"
                      label={
                        formXendit.active
                          ? "Aktif (Dapat Dipilih)"
                          : "Nonaktif"
                      }
                      checked={formXendit.active}
                      onChange={(e) =>
                        setFormXendit({
                          ...formXendit,
                          active: e.target.checked,
                        })
                      }
                      disabled={submitting}
                    />
                  </div>

                  {/* Webhook info box */}
                  <div className="p-3 bg-light border rounded small">
                    <div className="fw-bold mb-1">
                      Petunjuk Konfigurasi Webhook Xendit:
                    </div>
                    <div>
                      Daftarkan webhook URL berikut di Dashboard Xendit Anda:
                    </div>
                    <div className="mt-1">
                      <code className="p-1 bg-white border rounded">
                        /api/v1/transaction/notification/xendit
                      </code>
                    </div>
                    <div className="mt-1 text-muted">
                      Pastikan Webhook Verification Token yang diberikan Xendit
                      disalin sama persis ke form di atas.
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-light border"
                    onClick={() => setViewMode("list")}
                    disabled={submitting}
                  >
                    Batal
                  </button>
                  <ButtonPrimary type="submit" disabled={submitting}>
                    {submitting ? "Menyimpan..." : "Simpan Konfigurasi XENDIT"}
                  </ButtonPrimary>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </Modal_Component>
  );
};

// STYLED COMPONENTS
const BannerCard = styled.div`
  background: #ffffff;
  border-radius: 8px;
  border-left: 5px solid ${COLOR_PRIMARY};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const GatewayCard = styled.div`
  background: #ffffff;
  border-radius: 8px;
  border: ${(props) =>
    props.$isSelected
      ? `2px solid #28a745`
      : props.$isConfigured
      ? "1px solid #ced4da"
      : "1px dashed #adb5bd"};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease-in-out;
`;

const ProviderChoiceCard = styled.div`
  background: #ffffff;
  border-radius: 8px;
  border: ${(props) =>
    props.$isConfigured ? "1px solid #ced4da" : `1px solid ${COLOR_PRIMARY}`};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease-in-out;
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const BadgeSuccess = styled.span`
  background-color: #28a745;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 12px;
  display: inline-block;
`;

const BadgeInfo = styled.span`
  background-color: #17a2b8;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 12px;
  display: inline-block;
`;

const BadgeWarning = styled.span`
  background-color: #ffc107;
  color: #212529;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 12px;
  display: inline-block;
`;

const BadgeMuted = styled.span`
  background-color: #6c757d;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 12px;
  display: inline-block;
`;
